import React, { useState, useEffect } from 'react';
import { Task, TaskFormData } from '../types/Task';
import { taskApi } from '../services/taskApi';
import TaskForm from './TaskForm';
import { 
  PlusIcon, 
  ClockIcon, 
  CalendarIcon, 
  UserGroupIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

interface LeadTasksTabProps {
  leadId: string;
  leadName: string;
  leadEmail: string;
  leadPhone?: string;
}

const LeadTasksTab: React.FC<LeadTasksTabProps> = ({ 
  leadId, 
  leadName, 
  leadEmail, 
  leadPhone 
}) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [taskFilter, setTaskFilter] = useState<'all' | 'my'>('all'); // New state for My Tasks vs All Tasks
  const [viewMyTasksOnly, setViewMyTasksOnly] = useState(false); // Permission to only view my tasks
  const [viewAllTasks, setViewAllTasks] = useState(true); // Permission to view all tasks
  const [permissionsLoaded, setPermissionsLoaded] = useState(false); // Track if permissions have been loaded

  // Fetch permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        // Use the shared API client (consistent base URL + auth + branch header)
        // instead of a raw fetch with a divergent hardcoded URL.
        const data = await taskApi.getTasksViewPermissions();
        setViewMyTasksOnly(data.viewMyTasksOnly || false);
        setViewAllTasks(data.viewAllTasks !== undefined ? data.viewAllTasks : true);

        // If user can only view their tasks, set filter to 'my' by default
        if (data.viewMyTasksOnly) {
          setTaskFilter('my');
        }
      } catch (err) {
        console.error('Error fetching tasks view permissions:', err);
        setViewAllTasks(true);
      } finally {
        setPermissionsLoaded(true);
      }
    };

    fetchPermissions();
  }, []);

  useEffect(() => {
    // Only fetch tasks if permissions have been loaded
    if (permissionsLoaded) {
      fetchTasks();
    }
  }, [leadId, taskFilter, permissionsLoaded]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get current user ID for "My Tasks" filter
      const currentUserStr = localStorage.getItem('leadshive_user');
      const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
      const currentUserId = currentUser?.id;
      
      // If user has permission to only view their tasks, always use their ID as filter
      // Otherwise, use the taskFilter state
      const assigneeId = (viewMyTasksOnly || taskFilter === 'my') ? (currentUserId?.toString() || '') : '';
      
      console.log('🔍 LeadTasksTab - Fetching tasks with filters:', {
        leadId,
        assigneeId,
        taskFilter,
        viewMyTasksOnly
      });
      
      // Use the main tasks API with leadId and assigneeId filtering
      const response = await taskApi.getTasks(
        1, // page
        100, // limit (get all tasks for this lead)
        '', // search
        '', // status
        '', // priority
        '', // categoryId
        assigneeId, // assigneeId (for My Tasks filter)
        leadId // leadId (filter by this specific lead)
      );
      
      console.log('✅ LeadTasksTab - Received tasks:', response.data?.length || 0, 'tasks');
      
      if (response.success) {
        setTasks(response.data);
      } else {
        setError('Failed to fetch tasks');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = () => {
    setEditingTask(null);
    setShowForm(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setShowForm(true);
  };

  const handleViewTask = (task: Task) => {
    // Navigate to task detail page
    window.open(`/tasks/${task.id}`, '_blank');
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      setDeletingId(taskId);
      await taskApi.deleteTask(taskId);
      await fetchTasks(); // Refresh the list
    } catch (err: any) {
      alert(err.message || 'Failed to delete task');
    } finally {
      setDeletingId(null);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingTask(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingTask(null);
    fetchTasks(); // Refresh the list
  };

  const handleTaskFilterChange = (filter: 'all' | 'my') => {
    setTaskFilter(filter);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'in_progress':
        return <ClockIcon className="w-4 h-4" />;
      case 'pending':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'cancelled':
        return <XCircleIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = (dueDate: string | undefined) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchTasks}
          className="text-blue-600 hover:underline"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Tasks for {leadName}</h3>
          <p className="text-sm text-gray-600">
            Manage tasks associated with this lead
          </p>
        </div>
        <button
          onClick={handleCreateTask}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Create Task
        </button>
      </div>

      {/* Task Filter Toggle - Only show if user can view all tasks */}
      {viewAllTasks && (
        <div className="flex justify-between items-center">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
            <button
              onClick={() => handleTaskFilterChange('all')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                taskFilter === 'all'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              All Tasks
            </button>
            <button
              onClick={() => handleTaskFilterChange('my')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                taskFilter === 'my'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              My Tasks
            </button>
          </div>
          {taskFilter === 'my' && (
            <p className="text-sm text-blue-600 font-medium">
              Showing tasks assigned to you or where you're supervising
            </p>
          )}
        </div>
      )}

      {/* Show message if user can only view their tasks */}
      {viewMyTasksOnly && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            You have permission to view only your assigned tasks in this branch.
          </p>
        </div>
      )}

      {/* Tasks List */}
      {tasks.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Yet</h3>
          <p className="text-gray-600 mb-4">
            This lead doesn't have any tasks assigned yet.
          </p>
          <button
            onClick={handleCreateTask}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Create First Task
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {tasks.map((task) => (
            <div
              key={task.id}
              className={`bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow ${
                isOverdue(task.dueDate) && task.status !== 'completed' ? 'border-red-200 bg-red-50' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="text-lg font-medium text-gray-900 truncate">
                      {task.name}
                    </h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                      {getStatusIcon(task.status)}
                      <span className="ml-1 capitalize">{task.status.replace('_', ' ')}</span>
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                      {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                    </span>
                    {isOverdue(task.dueDate) && task.status !== 'completed' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Overdue
                      </span>
                    )}
                  </div>

                  {task.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {task.description}
                    </p>
                  )}

                  <div className="flex items-center space-x-6 text-sm text-gray-500">
                    {task.startDate && (
                      <div className="flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-1" />
                        <span>Start: {formatDate(task.startDate)}</span>
                      </div>
                    )}
                    {task.dueDate && (
                      <div className="flex items-center">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        <span>Due: {formatDate(task.dueDate)}</span>
                      </div>
                    )}
                    {task.reminderDate && (
                      <div className="flex items-center">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        <span>Reminder: {formatDate(task.reminderDate)}</span>
                      </div>
                    )}
                  </div>

                  {/* Assignees and Supervisors */}
                  {((task.assignees && task.assignees.length > 0) || (task.supervisors && task.supervisors.length > 0)) && (
                    <div className="mt-3 flex items-center space-x-4">
                      {task.assignees && task.assignees.length > 0 && (
                        <div className="flex items-center text-sm">
                          <UserGroupIcon className="w-4 h-4 mr-1 text-green-600" />
                          <span className="text-gray-600">Assignees:</span>
                          <div className="flex flex-wrap gap-1 ml-1">
                            {task.assignees.map((assignee, index) => (
                              <span
                                key={assignee.id}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                              >
                                {assignee.firstName || assignee.first_name} {assignee.lastName || assignee.last_name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {task.supervisors && task.supervisors.length > 0 && (
                        <div className="flex items-center text-sm">
                          <UserGroupIcon className="w-4 h-4 mr-1 text-purple-600" />
                          <span className="text-gray-600">Supervisors:</span>
                          <div className="flex flex-wrap gap-1 ml-1">
                            {task.supervisors.map((supervisor, index) => (
                              <span
                                key={supervisor.id}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                              >
                                {supervisor.firstName || supervisor.first_name} {supervisor.lastName || supervisor.last_name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleViewTask(task)}
                    className="p-2 text-gray-400 hover:text-blue-600"
                    title="View Task"
                  >
                    <EyeIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEditTask(task)}
                    className="p-2 text-gray-400 hover:text-green-600"
                    title="Edit Task"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteTask(task.id)}
                    disabled={deletingId === task.id}
                    className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-50"
                    title="Delete Task"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Task Form Modal */}
      {showForm && (
        <TaskForm
          task={editingTask}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
          preSelectedLead={{
            id: parseInt(leadId),
            name: leadName,
            email: leadEmail,
            phone: leadPhone || ''
          }}
        />
      )}
    </div>
  );
};

export default LeadTasksTab;
